import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import { hideVisually } from 'polished';

import { trackingId } from '@blueprint/ui';

export const NO_ANNOUNCEMENT_TIMEOUT = 200;
export const DEFAULT_REPEAT_INTERVAL = 5000;

const AnnouncerBox = styled.span`
  ${({ showLabel }) => (!showLabel ? hideVisually() : undefined)}
`;

/**
 * Announcer
 */
function Announcer({
  repeat = false,
  repeatInterval = DEFAULT_REPEAT_INTERVAL,
  tone = 'polite',
  showLabel = false,
  children,
  ...rest
}) {
  const timerResets = useRef({});
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    /**
     * Handle announcement
     */
    const handleAnnouncement = () => {
      timerResets.current.announceId = setTimeout(
        () => setAnnouncement(children),
        NO_ANNOUNCEMENT_TIMEOUT,
      );
    };

    /**
     * Handle re-announcement
     */
    const handleReanounce = () => {
      timerResets.current.resetId = setInterval(() => {
        setAnnouncement(null);
        handleAnnouncement();
      }, repeatInterval);
    };

    // initial announcement
    handleAnnouncement();

    if (repeat) {
      // repeating then reannounce after a given time
      handleReanounce();
    }

    return () => {
      // clear all intervals on unMount
      Object.values(timerResets.current).forEach(clearInterval);
      timerResets.current = {};
    };
  }, [children, repeat, repeatInterval]);

  return (
    <AnnouncerBox
      data-testid="Announcer"
      data-trackid={trackingId('Announcer')}
      aria-live={tone}
      showLabel={showLabel}
      {...rest}
    >
      {announcement}
    </AnnouncerBox>
  );
}

Announcer.defaultProps = {
  repeat: false,
  repeatInterval: 5000,
  tone: 'polite',
  showLabel: false,
};

export default Announcer;
